import {Button, IconButton} from "@mui/material";
import {FiPlus, FiTrash2} from "react-icons/fi";
import {useLocation, useNavigate} from "react-router-dom";
import {useCallback} from "react";
import ListView from "../components/ListView/ListView";
import Window from "../components/Window/Window";
import SearchBar from "../components/ui/SearchBar";
import {productsColumns} from "../constants/dataGridColumns";
import {productSearchFields} from "../constants/searchFields";
import useApiRequest from "../hooks/useApiRequest";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import useConfirmDialog from "../hooks/useConfirmDialog";
import usePaginationSearch from "../hooks/usePaginationSearch";
import {snackbar} from "../utils/snackbar";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../hooks/useAuthPermissions";

const Products = () => {
  const navigate = useNavigate();
  const {state} = useLocation();
  const {loading, list, remove} = useApiRequest({baseUrl: "/products", resource: "products"});
  const {can} = useAuthPermissions();

  const fetcher = useCallback(
    async (params) => {
      return await list(params);
    },
    [list]
  );

  const {filters, applyFilters, resetFilters, dataGridProps, refresh, listState} = usePaginationSearch({
    fetcher,
    initialFilters: {},
    initialListState: state?.listState
  });

  const handleProductClick = (productId) => {
    if (productId) {
      navigate(`/product-form`, {state: {id: productId, from: {pathname: "/products", listState}}});
    }
  };

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const {success} = await remove(id);
    if (!success) return;

    snackbar.success("Producto eliminado correctamente.");
    refresh();
  });

  const handleOpenDeleteDialog = (id) => {
    const rows = dataGridProps.rows || [];
    const found = rows.find((product) => product.id === id);
    openDialog(id, found?.name || "");
  };

  const columns = [
    ...productsColumns.filter((col) => col.field !== "email"),
    ...(can(ACTIONS.DELETE, RESOURCES.PRODUCTS)
      ? [
          {
            field: "actions",
            headerName: "Actions",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                aria-label="delete"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDeleteDialog(params.row.id);
                }}
              >
                <FiTrash2 />
              </IconButton>
            )
          }
        ]
      : [])
  ];

  return (
    <Window
      title={"Productos"}
      topRightButtons={
        can(ACTIONS.WRITE, RESOURCES.PRODUCTS) && (
          <Button variant="contained" startIcon={<FiPlus />} onClick={() => navigate("/product-form")}>
            Nuevo Producto
          </Button>
        )
      }
    >
      {/* Barra de filtros */}
      <SearchBar fields={productSearchFields} initialValues={filters} onSearch={applyFilters} onReset={resetFilters} />

      <ListView
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        dataGridProps={{
          ...dataGridProps,
          paginationMode: "client",
          rowCount: undefined
        }}
        onRowClick={(row) => handleProductClick(row.id)}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="producto"
        entityLabel={itemName}
      />
    </Window>
  );
};

export default Products;
